import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { sortTemplatesByDefault } from '@realadvisor/common-types';

import { gql } from '@graphql/__generated__';
import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { DOC_TEMPLATE_THEMES } from '@modules/_shared/common/queries';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import type { ListingBrochureType } from '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditor';
import {
  UPDATE_BROCHURE_COVER,
  UPDATE_TEAM_BRANDING_COLOR_BROCHURE,
} from '@modules/reports/brochure-report/brochureReportsQueries';
import { ReportSettingsSection } from '@modules/reports/shared/ReportSettingsSection';
import { useRefetchWhenMissing } from '@modules/reports/shared/useRefetchWhenMissing';
import { useDebouncedHandler } from '@shared/hooks/debounce';
import type { Language } from '@shared/LocaleContext';

const GET_BROCHURE_TEMPLATES = gql(/* GraphQL */ `
  query GetListingBrochureTemplates {
    brochure_templates: doc_templates(
      limit: 100
      where: { type: { _eq: lot_brochure }, active: { _eq: true } }
      order_by: { name: asc }
    ) {
      id
      name
      ...DocTemplateDefaultLinks
    }
  }
`);

type BrochureSettingsForm = {
  font_family: string | null;
  doc_template_id: string;
  doc_template_theme_id: string | null;
  branding_color: string | null;
  language: Language;
};

type BrochureSettingsProps = {
  listingId: string;
  listingBrochure: ListingBrochureType;
  tenantId: string | null | undefined;
  previewLanguage: Language;
  onPreviewLanguageChange: (language: Language) => void;
  onTemplateContentChange: () => void;
};

export const BrochureSettings = ({
  listingId,
  listingBrochure,
  tenantId,
  previewLanguage,
  onPreviewLanguageChange,
  onTemplateContentChange,
}: BrochureSettingsProps) => {
  const [error, setError] = useState<ErrorLike | null>(null);
  const {
    data: templatesData,
    error: templatesError,
    refetch: refetchTemplates,
  } = useQuery(GET_BROCHURE_TEMPLATES);
  const currentTemplateId = listingBrochure.doc_template?.id;
  useRefetchWhenMissing(
    currentTemplateId,
    templatesData?.brochure_templates,
    refetchTemplates,
    setError,
  );
  const {
    data: themesData,
    error: themesError,
    refetch: refetchThemes,
  } = useQuery(DOC_TEMPLATE_THEMES, {
    variables: { type: Doc_Template_Type_Enum_Enum.LotBrochureTheme },
  });
  useRefetchWhenMissing(
    listingBrochure.doc_template_theme_id,
    themesData?.doc_templates,
    refetchThemes,
    setError,
  );

  useEffect(() => {
    const queryError = templatesError ?? themesError;
    if (queryError != null) {
      setError(queryError);
    }
  }, [templatesError, themesError]);

  const [updateBrochureCover] = useMutation(UPDATE_BROCHURE_COVER, {
    onError: setError,
  });
  const [updateTeamBrandingColor] = useMutation(
    UPDATE_TEAM_BRANDING_COLOR_BROCHURE,
    { onError: setError },
  );
  const refreshTemplateContentRef = useRef(false);

  const updateBrochureSettings = useCallback(
    async (formData: Partial<BrochureSettingsForm>) => {
      const refreshTemplateContent = refreshTemplateContentRef.current;
      refreshTemplateContentRef.current = false;
      await updateBrochureCover({
        variables: {
          id: listingId,
          input: {
            brochure_font_family: formData.font_family,
            brochure_doc_template_id: formData.doc_template_id || undefined,
            doc_template_theme_id: formData.doc_template_theme_id ?? null,
          },
        },
      });
      if (refreshTemplateContent) {
        onTemplateContentChange();
      }
    },
    [listingId, onTemplateContentChange, updateBrochureCover],
  );
  const debouncedUpdateBrochureSettings = useDebouncedHandler(
    300,
    updateBrochureSettings,
  );

  const updateBrandingColor = useCallback(
    async (brandingColor: string | null) => {
      const teamId = listingBrochure.broker?.default_team?.id;
      if (teamId == null) {
        return;
      }
      await updateTeamBrandingColor({
        variables: {
          teamId,
          brandingColor,
          reportId: listingId,
        },
      });
    },
    [
      listingBrochure.broker?.default_team?.id,
      listingId,
      updateTeamBrandingColor,
    ],
  );
  const debouncedUpdateBrandingColor = useDebouncedHandler(
    300,
    updateBrandingColor,
  );

  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const teamId = listingBrochure.broker?.default_team?.id;

  const sortedTemplates = useMemo(() => {
    return sortTemplatesByDefault(templatesData?.brochure_templates ?? [], {
      tenantId,
      teamId,
    }).map(template => ({ id: template.id, name: template.name }));
  }, [templatesData, tenantId, teamId]);

  const onChangeHandler: RaFormOnChange<BrochureSettingsForm> = useCallback(
    (formData, name, type) => {
      if (type !== 'change') {
        return;
      }

      if (name === 'language') {
        if (formData.language != null) {
          onPreviewLanguageChange(formData.language);
        }
        return;
      }

      if (name === 'branding_color') {
        debouncedUpdateBrandingColor(formData.branding_color ?? null);
        return;
      }

      refreshTemplateContentRef.current ||=
        name === 'doc_template_id' || name === 'doc_template_theme_id';
      debouncedUpdateBrochureSettings(formData);
    },
    [
      debouncedUpdateBrandingColor,
      debouncedUpdateBrochureSettings,
      onPreviewLanguageChange,
    ],
  );

  const formDefinition: FormDefinitionType<BrochureSettingsForm> = useCallback(
    () => [
      {
        type: 'custom',
        name: 'settings_section',
        element: (
          <ReportSettingsSection
            expanded={settingsExpanded}
            onToggle={() => setSettingsExpanded(prev => !prev)}
            docTemplatesData={sortedTemplates}
            themeTemplatesData={themesData?.doc_templates}
            currentTemplateId={currentTemplateId}
            currentThemeId={listingBrochure.doc_template_theme_id}
            currentThemeName={listingBrochure.doc_template_theme?.name}
            includeLanguage
          />
        ),
        gridProps: { md: 12 },
      },
    ],
    [
      settingsExpanded,
      sortedTemplates,
      currentTemplateId,
      themesData?.doc_templates,
      listingBrochure.doc_template_theme_id,
      listingBrochure.doc_template_theme?.name,
    ],
  );

  return (
    <>
      <RaForm
        freezeInitialDefaultValues
        gridProps={{ sx: { p: 0 } }}
        formDefinition={formDefinition}
        defaultValues={{
          font_family: listingBrochure.brochure_font_family || 'default',
          doc_template_id: listingBrochure.doc_template?.id || '',
          doc_template_theme_id: listingBrochure.doc_template_theme_id ?? null,
          branding_color:
            listingBrochure.broker?.default_team?.branding_color || null,
          language: previewLanguage,
        }}
        onSubmit={async () => {}}
        onChange={onChangeHandler}
        actionButtonsComponent={<></>}
      />
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </>
  );
};

export default BrochureSettings;
